import React, { useEffect, useRef, useState } from 'react';
import './CustomCursor.css';
import { shouldEnableCustomCursor, TARGETING_CURSOR_SUPPRESS_CLASS } from '../config/cursorConfig';

/**
 * CustomCursor Component
 *
 * Renders a custom cursor that is visible in Discord/OBS streams.
 * The Windows cursor is not captured by screen recording software,
 * but this React-rendered cursor is.
 *
 * Performance:
 * - Position is written to the DOM via ref + rAF (no React re-render per move).
 * - Pointer styling uses getComputedStyle only when event.target changes.
 * - MutationObserver keeps transparent cursor on new nodes; no periodic querySelectorAll('*').
 */

function computeIsPointer(target) {
  if (!target || target.nodeType !== 1) return false;
  const cursorStyle = window.getComputedStyle(target).cursor;
  return (
    cursorStyle === 'pointer' ||
    target.tagName === 'BUTTON' ||
    target.tagName === 'A' ||
    target.onclick !== null ||
    target.classList.contains('clickable')
  );
}

const CustomCursor = () => {
  const enabled = shouldEnableCustomCursor();
  const elRef = useRef(null);
  const posRef = useRef({ x: -100, y: -100 });
  const lastTargetRef = useRef(null);
  const rafIdRef = useRef(null);

  const [battleTargetingReticleActive, setBattleTargetingReticleActive] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains(TARGETING_CURSOR_SUPPRESS_CLASS);
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const sync = () => setBattleTargetingReticleActive(root.classList.contains(TARGETING_CURSOR_SUPPRESS_CLASS));
    const mo = new MutationObserver(sync);
    mo.observe(root, { attributes: true, attributeFilter: ['class'] });
    sync();
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    if (!enabled || battleTargetingReticleActive) return undefined;

    const transparentCursor =
      "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAAtJREFUGFdjYAACAAAFAAGq1chRAAAAAElFTkSuQmCC'), none";

    document.body.style.setProperty('cursor', transparentCursor, 'important');
    document.documentElement.style.setProperty('cursor', transparentCursor, 'important');

    const overrideCursor = (element) => {
      if (element && element.style) {
        const isModalElement =
          element.classList?.contains('modal-overlay') ||
          element.classList?.contains('modal-content') ||
          element.classList?.contains('confirmation-overlay') ||
          element.classList?.contains('confirmation-dialog') ||
          element.classList?.contains('notification-toast') ||
          element.closest?.('.modal-overlay') ||
          element.closest?.('.modal-content') ||
          element.closest?.('.confirmation-overlay') ||
          element.closest?.('.confirmation-dialog') ||
          element.closest?.('.notification-toast');

        const isInputInModal =
          (element.tagName === 'INPUT' ||
            element.tagName === 'TEXTAREA' ||
            element.tagName === 'SELECT') &&
          (element.closest?.('.modal-overlay') ||
            element.closest?.('.modal-content') ||
            element.closest?.('.confirmation-overlay') ||
            element.closest?.('.confirmation-dialog'));

        if (isModalElement || isInputInModal) {
          return; // Don't override cursor for modal elements or inputs in modals
        }
        element.style.setProperty('cursor', transparentCursor, 'important');
      }
    };

    const overrideAllCursors = () => {
      overrideCursor(document.body);
      overrideCursor(document.documentElement);
      const allElements = document.querySelectorAll('*');
      allElements.forEach(overrideCursor);
    };

    overrideAllCursors();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            overrideCursor(node);
            const children = node.querySelectorAll('*');
            children.forEach(overrideCursor);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    const flushPosition = () => {
      rafIdRef.current = null;
      const el = elRef.current;
      if (!el) return;
      const { x, y } = posRef.current;
      el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
    };

    const scheduleFlush = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = window.requestAnimationFrame(flushPosition);
    };

    const handleMouseMove = (e) => {
      posRef.current = { x: e.clientX, y: e.clientY };

      if (lastTargetRef.current !== e.target) {
        lastTargetRef.current = e.target;
        const isPointer = computeIsPointer(e.target);
        const el = elRef.current;
        if (el) {
          el.classList.toggle('custom-cursor-pointer', isPointer);
        }
      }

      scheduleFlush();
    };

    const handleMouseLeave = () => {
      posRef.current = { x: -100, y: -100 };
      lastTargetRef.current = null;
      const el = elRef.current;
      if (el) {
        el.classList.remove('custom-cursor-pointer');
      }
      scheduleFlush();
    };

    const handleMouseEnter = (e) => {
      posRef.current = { x: e.clientX, y: e.clientY };
      const surface = elRef.current;
      if (surface && typeof document.elementFromPoint === 'function') {
        const t = document.elementFromPoint(e.clientX, e.clientY);
        lastTargetRef.current = t || null;
        if (t) {
          surface.classList.toggle('custom-cursor-pointer', computeIsPointer(t));
        } else {
          surface.classList.remove('custom-cursor-pointer');
        }
      } else if (surface) {
        lastTargetRef.current = null;
        surface.classList.remove('custom-cursor-pointer');
      }
      scheduleFlush();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      observer.disconnect();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('mouseenter', handleMouseEnter);
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [enabled, battleTargetingReticleActive]);

  if (!enabled || battleTargetingReticleActive) {
    return null;
  }

  return <div ref={elRef} className="custom-cursor" />;
};

export default CustomCursor;
